// ============================================================
// Axios Instance — API Layer
// ============================================================
// All API calls go through this instance. Components never
// import axios directly — they use services which use this.

import axios from 'axios';
import config from '@/config';
import useAuthStore from '@/store/authStore';
import useNetworkStore from '@/store/networkStore';

const apiClient = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
});
// ── Request Interceptor ──────────────────────────────────────
apiClient.interceptors.request.use(
  (reqConfig) => {
    // Check network availability to prevent data loss when offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      useNetworkStore.getState().setOffline(true);
      const offlineError = new Error('No internet connection. API calls are paused.');
      offlineError.isOffline = true;
      return Promise.reject(offlineError);
    }

    // Attach auth token (handle Zustand persisted state format)
    const authStorage = localStorage.getItem(config.storage.tokenKey);
    if (authStorage) {
      try {
        const parsed = JSON.parse(authStorage);
        const token = parsed?.state?.token;
        if (token) {
          reqConfig.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Fallback in case it's not JSON (just raw string)
        reqConfig.headers.Authorization = `Bearer ${authStorage}`;
      }
    }

    // Attach current school ID for multi-school context
    const schoolData = localStorage.getItem(config.storage.schoolKey);
    if (schoolData) {
      try {
        const parsedSchool = JSON.parse(schoolData);
        const schoolId = parsedSchool?.state?.currentSchool?.id || parsedSchool?.id;
        if (schoolId) {
          reqConfig.headers['X-School-Id'] = schoolId;
        }
      } catch {
        // ignore parse errors
      }
    }

    return reqConfig;
  },
  (error) => Promise.reject(error)
);

// ── Response Interceptor ─────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;

    if (data) {
      if (typeof data.detail === 'string') {
        error.message = data.detail;
      } else if (Array.isArray(data.detail) && data.detail.length > 0) {
        error.message = data.detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(', ');
      } else if (typeof data.message === 'string') {
        error.message = data.message;
      } else if (typeof data.error === 'string') {
        error.message = data.error;
      }
    }

    if (status === 401) {
      // Token expired or invalid — show session expired popup modal
      const requestUrl = error.config?.url || '';
      const isLoginRequest = requestUrl.includes('/login') || requestUrl.includes('/auth/login');
      if (!isLoginRequest && window.location.pathname !== '/login') {
        useAuthStore.getState().setSessionExpired(true);
      }
    }

    if (status === 403) {
      // Forbidden — could redirect to unauthorized
      console.warn('Access forbidden:', error.message);
    }

    if (status >= 500) {
      console.error('Server error:', error.message);
      const serverDetail = data?.detail || data?.message || data?.error;
      if (serverDetail) {
        error.message = typeof serverDetail === 'string' ? serverDetail : JSON.stringify(serverDetail);
      } else if (!error.message || error.message.includes('status code')) {
        error.message = `500 Internal Server Error: The server encountered an error processing your request.`;
      }
    }

    if (!error.response) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        useNetworkStore.getState().setOffline(true);
        error.message = `No internet connection. Please check your network connection.`;
      } else {
        error.message = error.message && error.message !== 'Network Error'
          ? error.message
          : `Server Error: Unable to receive response from the server. Please check backend server status.`;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
